/**
 * Modal opened when a save returns a `409 Conflict` because a `FieldName` is
 * defined on both Anverso and Reverso. The operator orders the two
 * candidates per conflicted field and the result is sent back as
 * `prioritizationOverrides` on retry.
 */
import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Button, IconButton, Stack, Tooltip, useMediaQuery, useTheme,
} from '@mui/material';
import { KeyboardArrowUpRounded as MoveUpIcon, KeyboardArrowDownRounded as MoveDownIcon } from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import type { FieldConflict, FieldCandidate, PrioritizationOverrides, DocumentSideName } from './bothComposition.types';

interface Props {
  open: boolean;
  conflicts: FieldConflict[];
  /** Look up KYC field display name by id. */
  kycFieldNameById?: Record<string, string>;
  onCancel: () => void;
  onConfirm: (overrides: PrioritizationOverrides) => void;
}

const SIDE_KEYS: Record<DocumentSideName, string> = {
  Front: 'fields-mapping.both.side_front',
  Back: 'fields-mapping.both.side_back',
};

/** One side's version of the conflicted field, with its place in the order. */
const CandidateCard: React.FC<{
  candidate: FieldCandidate;
  position: number;
  total: number;
  onMoveUp: () => void;
  onMoveDown: () => void;
  kycFieldNameById?: Record<string, string>;
}> = ({ candidate, position, total, onMoveUp, onMoveDown, kycFieldNameById }) => {
  const t = useTranslate();
  const theme = useTheme();
  const mono = { fontFamily: theme.custom.text.mono, overflowWrap: 'anywhere' } as const;
  const moveUp = t('fields-mapping.both.priority_dialog.move_up');
  const moveDown = t('fields-mapping.both.priority_dialog.move_down');
  return (
    <Box
      sx={{
        flex: '1 1 16rem',
        minWidth: 0,
        p: 1.5,
        border: 1,
        borderColor: 'divider',
        borderRadius: theme.custom.radius.large,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {position + 1}. {t(SIDE_KEYS[candidate.source])}
        </Typography>
        <Tooltip title={moveUp}>
          <span>
            <IconButton onClick={onMoveUp} disabled={position === 0} aria-label={moveUp}>
              <MoveUpIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={moveDown}>
          <span>
            <IconButton onClick={onMoveDown} disabled={position === total - 1} aria-label={moveDown}>
              <MoveDownIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {candidate.regex && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('fields-mapping.both.priority_dialog.regex_label')}
          </Typography>
          <Typography variant="body2" sx={mono}>{candidate.regex}</Typography>
        </Box>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {t('fields-mapping.both.priority_dialog.sources_label')}
      </Typography>
      <Box component="ol" sx={{ m: 0, pl: 3 }}>
        {(candidate.kycFields || []).map((k, i) => (
          <Typography component="li" variant="body2" sx={mono} key={i}>
            {(k.kycFieldId && kycFieldNameById?.[k.kycFieldId]) || (k.prefix ? `"${k.prefix}"` : '—')}
          </Typography>
        ))}
      </Box>
    </Box>
  );
};

export const PriorityResolutionDialog: React.FC<Props> = ({
  open,
  conflicts,
  kycFieldNameById,
  onCancel,
  onConfirm,
}) => {
  const t = useTranslate();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const initialOrder: Record<string, DocumentSideName[]> = useMemo(() => {
    const o: Record<string, DocumentSideName[]> = {};
    for (const c of conflicts) {
      o[c.fieldName] = c.candidates.map((cand) => cand.source);
    }
    return o;
  }, [conflicts]);

  const [order, setOrder] = useState<Record<string, DocumentSideName[]>>(initialOrder);

  React.useEffect(() => {
    setOrder(initialOrder);
  }, [initialOrder]);

  const move = (fieldName: string, fromIdx: number, direction: -1 | 1) => {
    setOrder((prev) => {
      const arr = [...(prev[fieldName] || [])];
      const target = fromIdx + direction;
      if (target < 0 || target >= arr.length) return prev;
      [arr[fromIdx], arr[target]] = [arr[target], arr[fromIdx]];
      return { ...prev, [fieldName]: arr };
    });
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth fullScreen={isPhone}>
      <DialogTitle>{t('fields-mapping.both.priority_dialog.title')}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('fields-mapping.both.priority_dialog.subtitle')}
        </Typography>

        <Stack spacing={3}>
          {conflicts.map((conflict) => {
            const currentOrder = order[conflict.fieldName] || conflict.candidates.map((c) => c.source);
            const orderedCandidates = currentOrder
              .map((src) => conflict.candidates.find((c) => c.source === src))
              .filter((c): c is FieldCandidate => Boolean(c));

            return (
              <Box key={conflict.fieldName} role="group" aria-label={conflict.fieldName}>
                <Stack direction="row" alignItems="baseline" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontFamily: theme.custom.text.mono }}>
                    {conflict.fieldName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('fields-mapping.both.priority_dialog.conflict_hint')}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                  {orderedCandidates.map((cand, idx) => (
                    <CandidateCard
                      key={cand.source}
                      candidate={cand}
                      position={idx}
                      total={orderedCandidates.length}
                      kycFieldNameById={kycFieldNameById}
                      onMoveUp={() => move(conflict.fieldName, idx, -1)}
                      onMoveDown={() => move(conflict.fieldName, idx, 1)}
                    />
                  ))}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onCancel}>
          {t('fields-mapping.both.priority_dialog.cancel')}
        </Button>
        <Button variant="contained" onClick={() => onConfirm(order)}>
          {t('fields-mapping.both.priority_dialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
